import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Mail, CheckCircle2, Loader2, Send } from "lucide-react";
import { useLang } from "@/lang";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) throw new Error("fail");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-bei-brand focus:ring-4 focus:ring-bei-brand/10";

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-white py-20 sm:py-28 lg:py-36">
      <div className="absolute -left-40 bottom-0 h-[400px] w-[400px] rounded-full bg-bei-cyan/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-bei-navy sm:text-4xl lg:text-5xl">
                {t.contact.title}
                <span className="block text-bei-brand">{t.contact.titleAccent}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
                {t.contact.subtitle}
              </p>
            </Reveal>

            <div className="mt-12 space-y-5">
              {[
                { icon: MapPin, label: t.contact.addressLabel, value: t.contact.address, testid: "contact-info-address" },
                { icon: Phone, label: t.contact.phoneLabel, value: "01 61 12 25 39", testid: "contact-info-phone", href: "tel:+33161122539" },
                { icon: Mail, label: t.contact.emailLabel, value: "contact@beintec.com", testid: "contact-info-email", href: "mailto:contact@beintec.com" },
              ].map((c, i) => (
                <Reveal key={i} delay={0.25 + i * 0.08}>
                  <div data-testid={c.testid} className="group flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-bei-navy text-bei-cyan transition-colors duration-300 group-hover:bg-bei-brand group-hover:text-white">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {c.label}
                      </p>
                      {c.href ? (
                        <a href={c.href} className="mt-1 block text-base font-semibold text-bei-navy transition-colors hover:text-bei-brand">
                          {c.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-base font-semibold text-bei-navy">{c.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-bei-mist p-7 shadow-[0_30px_80px_rgba(11,25,44,0.08)] sm:p-10">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success-state"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="flex min-h-[480px] flex-col items-center justify-center text-center"
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                      className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100"
                    >
                      <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                    </motion.span>
                    <h3 className="mt-8 font-display text-2xl font-bold text-bei-navy">
                      {t.contact.successTitle}
                    </h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                      {t.contact.successMsg}
                    </p>
                    <button
                      data-testid="contact-send-again-button"
                      onClick={() => {
                        setForm({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
                        setStatus("idle");
                      }}
                      className="mt-8 rounded-full border border-bei-brand px-6 py-3 text-sm font-semibold text-bei-brand transition-all duration-300 hover:bg-bei-brand hover:text-white"
                    >
                      {t.contact.again}
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -12 }}
                    className="space-y-5"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="c-name" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {t.contact.form.name} *
                        </label>
                        <input id="c-name" data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.form.namePh} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="c-company" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {t.contact.form.company}
                        </label>
                        <input id="c-company" data-testid="contact-input-company" value={form.company} onChange={set("company")} placeholder={t.contact.form.companyPh} className={inputCls} />
                      </div>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="c-email" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {t.contact.form.email} *
                        </label>
                        <input id="c-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.form.emailPh} className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="c-phone" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {t.contact.form.phone}
                        </label>
                        <input id="c-phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder={t.contact.form.phonePh} className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="c-subject" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {t.contact.form.subject} *
                      </label>
                      <select id="c-subject" data-testid="contact-select-subject" required value={form.subject} onChange={set("subject")} className={`${inputCls} ${form.subject ? "" : "text-slate-400"}`}>
                        <option value="" disabled>
                          {t.contact.form.subject}…
                        </option>
                        {t.contact.subjects.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="c-message" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {t.contact.form.message} *
                      </label>
                      <textarea id="c-message" data-testid="contact-textarea-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.form.messagePh} className={`${inputCls} resize-none`} />
                    </div>

                    {status === "error" && (
                      <p data-testid="contact-error-state" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                        {t.contact.error}
                      </p>
                    )}

                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={status === "sending"}
                      className="group flex w-full items-center justify-center gap-3 rounded-full bg-bei-brand px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure hover:shadow-[0_12px_40px_rgba(0,82,204,0.35)] disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t.contact.form.sending}
                        </>
                      ) : (
                        <>
                          {t.contact.form.submit}
                          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                        </>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
